import React, { Fragment, useMemo, useState } from 'react';
import { has, size, debounce, sumBy } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Message, FlexboxGrid, Table, Col, Icon, Divider } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { NameCell, QuantityCell, DeleteCell } from './CustomCells';
import {
  updatePosOrderItemQuantity,
  deletePosOrderItem
} from '../../../../redux/sagas/routines';
import { COMPLETED } from '../../../../constants/pos/OrderStatus';
import {
  updateItemAdjustment,
  deleteItemAdjustment
} from '../../../../redux/POS/actions';
import {
  getIsSavingItem,
  getSavingItemErrors,
  getSavingAdjustmentErrorItems
} from '../../../../redux/POS/selectors/order';
import toInventoryItemUniqKey from '../../../../helpers/toInventoryItemUniqKey';

const { Column, HeaderCell } = Table;

const EmptyItemsMessage = () => (
  <Message
    showIcon
    type="info"
    title="Your cart is empty."
    description="To start, scan an item or search for an item by its name using the search field above"
    style={{ margin: '10px 10px 0' }}
  />
);

function OrderItems({ isLoading, data, orderStatus }) {
  const dispatch = useDispatch();
  const isCompleted = orderStatus === COMPLETED;
  const [itemToDelete, setItemToDelete] = useState(false);
  const [itemToUpdate, setItemToUpdate] = useState(null);

  const isSavingItem = useSelector(getIsSavingItem);
  const savingItemErrors = useSelector(getSavingItemErrors);
  const savingAdjustmentErrorItems = useSelector(getSavingAdjustmentErrorItems);
  const savingItemsError = !isCompleted
    ? savingItemErrors
    : savingAdjustmentErrorItems;

  const cartItems = useMemo(() => sumBy('quantity', data), [data]);

  const updateOrderItem = debounce(600)((orderItem, quantity) => {
    setItemToUpdate(orderItem);
    dispatch(updatePosOrderItemQuantity({ orderItem, quantity }));
  });

  const handleQuantityChange = (orderItem, quantity) => {
    if (!isCompleted) {
      updateOrderItem(orderItem, quantity);
    } else {
      dispatch(updateItemAdjustment({ orderItem, quantity }));
    }
  };

  const handleDeleteItem = item => {
    if (!isCompleted) {
      setItemToDelete(item);
      dispatch(deletePosOrderItem(item));
    } else {
      dispatch(deleteItemAdjustment(item));
    }
  };

  const handleIsDeletingItem = ({ id }) => {
    if (!isCompleted) {
      return itemToDelete.id === id;
    }
    return false;
  };

  const handleIsUpdatingItem = ({ id }) => {
    if (itemToUpdate && !isCompleted) {
      return itemToUpdate.id === id && isSavingItem;
    }
    return false;
  };

  const handleIsItemHasError = (itemId, locationId, zoneId) => {
    const itemUniqKey = toInventoryItemUniqKey(itemId, locationId, zoneId);
    return has(itemUniqKey, savingItemsError);
  };

  return (
    <Fragment>
      <FlexboxGrid>
        <FlexboxGrid.Item componentClass={Col} xs={24}>
          <Divider style={{ marginTop: 20, marginBottom: 30 }}>
            <h6 style={{ color: '#357dbd' }}>
              <Icon icon="shopping-cart" size="lg" />
              &nbsp; My Cart ({isLoading ? '...' : `${size(data)}/${cartItems}`}
              )
            </h6>
          </Divider>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item componentClass={Col} xs={24}>
          <Table
            data={data}
            rowHeight={60}
            autoHeight
            bordered
            loading={isLoading}
            renderEmpty={EmptyItemsMessage}
          >
            <Column flexGrow={1}>
              <HeaderCell>
                <strong>Name</strong>
              </HeaderCell>
              <NameCell dataKey="meta.item.name" valuePath="meta" />
            </Column>
            <Column align="center" width={160}>
              <HeaderCell>
                <strong>Qty</strong>
              </HeaderCell>
              <QuantityCell
                dataKey="quantity"
                onChange={handleQuantityChange}
                isDeleting={handleIsDeletingItem}
                isItemHasError={handleIsItemHasError}
              />
            </Column>
            <Column align="center" width={60}>
              <HeaderCell />
              <DeleteCell
                dataKey="id"
                onDelete={handleDeleteItem}
                isUpdating={handleIsUpdatingItem}
                isDeleting={handleIsDeletingItem}
              />
            </Column>
          </Table>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    </Fragment>
  );
}

const OrderItemType = PropTypes.shape({});

OrderItems.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(OrderItemType).isRequired,
  orderStatus: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired
};

export default OrderItems;
